<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    import { faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import { AppointmentsRepository } from '../../modules/schedulerApi/appointments/appointments.repository'
    import { AppointmentsService } from '../../modules/schedulerApi/appointments/appointments.service'
    import { Appointment } from '../../modules/schedulerApi/appointments/appointment'
    import type { CreateAppointmentDto } from '../../modules/schedulerApi/appointments/dto/create-appointment.dto'
    import type { UpdateAppointmentDto } from '../../modules/schedulerApi/appointments/dto/upate-appointment.dto'
    import { AppointmentStatus } from '../../modules/schedulerApi/appointments/appointments.enum'
    import { ScheduleBlockRulesRepository } from '../../modules/schedulerApi/schedule-block-rules/schedule-block-rules.repository'
    import { ScheduleBlockRulesService } from '../../modules/schedulerApi/schedule-block-rules/schedule-block-rules.service'
    import type { CreateScheduleBlockRuleDto } from '../../modules/schedulerApi/schedule-block-rules/dto/create-schedule-block-rule.dto'
    import type { Schedule } from '../../modules/schedulerApi/schedules/schedule'
    import Zact from '../_common/z/zact.svelte'
    import Zframe from '../_common/z/zframe.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZinputSwitch from '../_common/z/zinput-switch.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import Popover from '../_common/popover.svelte'
    import BlockRuleForm from './block-rule-form.svelte'
    import { ancorarTerminoNoInicio, intervaloInicial } from './appointment-dates'
    // ##############################################################################################################
    const dispatch = createEventDispatcher()

    const entityId = 'appointmentId'

    const rep = new AppointmentsRepository()
    const service = new AppointmentsService(rep)

    const blockRuleRep = new ScheduleBlockRulesRepository()
    const blockRuleService = new ScheduleBlockRulesService(blockRuleRep)

    export let appointmentId: string = null
    export let dates: { start: Date; end: Date; scheduleId: string } = null

    let entity: Appointment = new Appointment()
    let show = false

    let recurring = false
    let schedules: Schedule[] = []
    let scheduleOptions: { value: string; label: string }[] = []

    let recurringScheduleIds: string[] = []
    let recurringWeekdays: number[] = [0, 1, 2, 3, 4, 5, 6]
    let recurringStartDate: Date = null
    let recurringEndDate: Date = null

    const weekdayOptions = [
        { value: 0, label: 'Domingo' },
        { value: 1, label: 'Segunda' },
        { value: 2, label: 'Terça' },
        { value: 3, label: 'Quarta' },
        { value: 4, label: 'Quinta' },
        { value: 5, label: 'Sexta' },
        { value: 6, label: 'Sábado' },
    ]

    async function loadSchedules() {
        schedules = await window.api.schedulers.schedules.getAll()
        schedules = schedules.filter((el) => el.status === 100)
        scheduleOptions = schedules.map((el) => ({ value: el.scheduleId, label: el.label }))
    }

    async function editEntity(id: string) {
        try {
            show = true
            entity = new Appointment()
            entity = await service.getOne(id)
            entity.start = new Date(entity.start)
            entity.end = new Date(entity.end)
            console.log('entity', entity)
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    // o campo de hora edita só HH:mm e mantém o dia do valor que já tinha; reancorar no início é o que
    // impede o par de ficar em dias diferentes (ver appointment-dates.ts)
    function endTimeChanged() {
        entity.end = ancorarTerminoNoInicio(entity.start, entity.end)
    }

    function newEntity() {
        entity = new Appointment()
        // nunca `new Date(dates.end)` direto: término ausente viraria 1970, não data inválida
        const intervalo = intervaloInicial(dates)
        entity.start = intervalo.start
        entity.end = intervalo.end
        entity.scheduleId = dates.scheduleId
        entity.status = AppointmentStatus.agendamento_bloqueado
        entity.items = []

        recurring = false
        recurringScheduleIds = [dates.scheduleId]
        // mesma razão: sem início, estes também cairiam em 1970 em vez de ficarem vazios
        recurringWeekdays = intervalo.start ? [intervalo.start.getDay()] : []
        recurringStartDate = intervalo.start
        recurringEndDate = intervalo.start
    }

    async function createEntity() {
        try {
            if (show) return
            show = true
            const dto: CreateAppointmentDto = {
                auto: false,
                end: entity.end,
                items: [],
                personId: null,
                scheduleId: entity.scheduleId,
                start: entity.start,
                status: AppointmentStatus.agendamento_bloqueado,
                notes: entity.notes,
                summary: 'Bloqueio' + (entity.notes ? ' / ' + entity.notes : ''),
                comissionSource: null,
                resources: null,
                source: 'erp',
            }
            await service.create(dto)
            dispatchChange()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            if (show) return
            show = true
            const dto: UpdateAppointmentDto = {
                end: entity.end,
                items: [],
                personId: null,
                scheduleId: entity.scheduleId,
                start: entity.start,
                status: entity.status,
                notes: entity.notes,
                summary: 'Bloqueio' + (entity.notes ? ' / ' + entity.notes : ''),
            }
            await service.update(entity[entityId], dto)
            dispatchChange()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function createRecurringRule() {
        try {
            if (show) return
            show = true
            // horário limpo devolve vazio (ver zinput-date-value) — sem isto, quebraria ao ler a hora abaixo
            if (!entity.start || !entity.end || isNaN(new Date(entity.start).getTime()) || isNaN(new Date(entity.end).getTime())) {
                evaluateError('Informe o horário de início e de término.')
                show = false
                return
            }
            const dto: CreateScheduleBlockRuleDto = {
                scheduleIds: recurringScheduleIds,
                weekdays: recurringWeekdays,
                startMinute: entity.start.getHours() * 60 + entity.start.getMinutes(),
                endMinute: entity.end.getHours() * 60 + entity.end.getMinutes(),
                startDate: recurringStartDate,
                endDate: recurringEndDate || undefined,
                notes: entity.notes,
            }
            if (!validateRecurringDto(dto)) {
                show = false
                return
            }
            await blockRuleService.create(dto)
            console.log('block', blockRuleService)
            dispatchChange()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function validateRecurringDto(dto: CreateScheduleBlockRuleDto) {
        if (!dto.scheduleIds || dto.scheduleIds.length === 0) {
            window.error('Selecione ao menos uma agenda para o bloqueio recorrente.')
            return false
        }
        if (!dto.startDate) {
            window.error('Informe a data de início do bloqueio recorrente.')
            return false
        }
        if (dto.endDate && +new Date(dto.endDate) < +new Date(dto.startDate)) {
            window.error('A data final deve ser posterior à data de início.')
            return false
        }
        if (dto.endMinute <= dto.startMinute) {
            window.error('O horário de término deve ser depois do horário de início.')
            return false
        }
        return true
    }

    async function saveEntity() {
        if (!entity[entityId] && recurring) {
            createRecurringRule()
        } else if (entity[entityId]) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este bloqueio?')) {
            return
        }
        try {
            show = true
            await service.remove(entity[entityId])
            dispatchChange()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        if (error === "Cannot read properties of undefined (reading 'personId')") {
            error = 'Selecione ou cadastre o cliente.!.'
        }
        window.error(error)
    }

    function dispatchChange() {
        dispatch('change')
        dispatch('close')
    }

    let showBlockRuleForm = false
    function editBlockRule() {
        showBlockRuleForm = true
    }

    function hideBlockRuleForm() {
        showBlockRuleForm = false
    }

    function blockRuleChanged() {
        showBlockRuleForm = false
        dispatchChange()
    }

    onMount(async () => {
        await loadSchedules()
        if (appointmentId) {
            await editEntity(appointmentId)
        } else {
            newEntity()
        }
    })
</script>

<LoadSpinner {show} />

{#if showBlockRuleForm}
    <Popover on:close={hideBlockRuleForm} title="Bloqueios recorrentes" width="900px">
        <BlockRuleForm scheduleBlockRuleId={entity.blockRuleId} on:close={hideBlockRuleForm} on:change={blockRuleChanged} />
    </Popover>
{/if}

<div class="root">
    <Zact>
        {#if window.store.token.permissions.scheduler.scheduler.write}
            {#if !!entity[entityId] && entity.blockRuleId}
                <Zbutton color="secondary" label="Editar bloqueios recorrentes" on:click={editBlockRule}></Zbutton>
            {/if}
            <div class="spacer"></div>
            {#if !!entity[entityId]}
                <Zbutton color="red" icon={faTrashAlt} label="Excluir" on:click={removeEntity}></Zbutton>
            {/if}
            <Zbutton color="primary" icon={faSave} label="Salvar" on:click={saveEntity}></Zbutton>
        {/if}
    </Zact>

    <Zframe class="m-3" title="Bloqueio de horários">
        <ZContainer gapRow="8px" gapCol="8px">
            <Zinput label="Início" size={6} sizeM={12} type="time" bind:value={entity.start} />
            <Zinput label="Término" size={6} sizeM={12} type="time" bind:value={entity.end} on:change={endTimeChanged} />
            <Zinput label="Motivo / anotações (opcional)" size={12} sizeM={12} type="text" bind:value={entity.notes} />
            {#if !entity[entityId]}
                <ZinputSwitch
                    class="mt-2"
                    label="Bloqueio recorrente"
                    size={12}
                    bind:value={recurring}
                    help="Repete este bloqueio em uma ou mais agendas, em dias da semana específicos, por um período ou permanentemente."
                />
            {/if}
        </ZContainer>

        {#if !entity[entityId] && recurring}
            <Zframe title="Configuração da recorrência" class="mt-2">
                <ZContainer gapRow="8px" gapCol="8px">
                    <Zinput
                        label="Agendas"
                        size={12}
                        sizeM={12}
                        type="multiselect"
                        options={scheduleOptions}
                        bind:value={recurringScheduleIds}
                        help="Selecione todas as agendas em que este bloqueio deve se repetir."
                    />
                    <Zinput
                        label="Dias da semana"
                        size={12}
                        sizeM={12}
                        type="multiselect"
                        options={weekdayOptions}
                        bind:value={recurringWeekdays}
                        help="Deixe todos marcados para repetir todos os dias."
                    />
                    <Zinput label="Data de início" size={6} sizeM={12} type="date" required={true} bind:value={recurringStartDate} />
                    <Zinput
                        label="Data final (opcional)"
                        size={6}
                        sizeM={12}
                        type="date"
                        bind:value={recurringEndDate}
                        help="Deixe em branco para um bloqueio permanente."
                    />
                </ZContainer>
            </Zframe>
        {/if}
    </Zframe>
</div>
